// S11 — MY SHIFT (spec 04 §4 floor row 11, §5 e16, slice 04-C-4).
//
// WHO READS THIS AND WHEN: one worker, on their own phone, mid-shift, standing
// at a machine, probably in gloves. They are asking one question — "how am I
// doing today?" — and they will look at it for about three seconds. So the
// screen is four numbers, each big enough to read at arm's length without
// focusing, in the order the question is actually asked: good first, then
// scrap, then steps finished, then piece quantity.
//
// SELF-ONLY BY CONSTRUCTION, and the screen inherits that for free: e16 takes
// no `worker_id` at all — the server aggregates the SESSION principal — so
// there is no id in this file, no picker, and nothing to get wrong. D-079's
// identity rule, applied to a read.
//
// NO MONEY, DELIBERATELY. §12 Q1 (who owns the piece-rate table) is OPEN, so
// this release can show a QUANTITY and cannot show a rate. Rendering a rate the
// module invented would put a number on a phone that a worker would expect on a
// payslip, and that is the one mistake a piece-rate screen must not make. The
// screen used to say so in a sentence; the copy budget (D-425) puts nothing on
// the glass for an absent feature, so it now says nothing (D-444 ruling 7).
//
// OFFLINE: `useCachedRead`, like every other floor read — the last snapshot
// with its age, never a spinner forever, and "never loaded" as its own state
// rather than four zeros that look like a bad shift.
//
// THE NUMBERS ARE RENDERED, NOT ECHOED (D-121, UX-5 surface review). Every
// quantity on the module-04 wire is a `numeric(14,3)` decimal STRING, and this
// screen printed all three of them verbatim: `420.000`, `18.000`, `420.000`.
// Three zeros that are always zero, on the one surface designed to be read in
// three seconds at arm's length in gloves, at 28–34 px where they are the
// widest thing on the tile — and at the moment a worker is comparing today
// against a number they remember. The office renders the identical value as
// `420` through `formatQty`, which also localizes the group and decimal
// separators, so the ru floor and the ru office disagreed about how a quantity
// is written. The date had the same shape of defect: `2026-08-07`, an ISO wire
// value printed as prose, on a product that has one date renderer precisely
// because the office once showed three formats on one step.
//
// The unit test pinned `"420.000"` as CORRECT (`my-shift.test.tsx:66`). A test
// that asserts a defect is worse than no test: it converts a bug into a
// contract, and the next person to render this number properly gets a red
// suite. Both moved together.

import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import type { components } from "@assessly/api-client";
import { client } from "../../../lib/api";
import { unwrap } from "../../../lib/request";
import {
  FloorError,
  FloorNeedsConnection,
  FloorScreen,
  FloorSkeleton,
  SnapshotNote,
} from "../components/floor-chrome";
import { floorReadSignal, useCachedRead } from "../hooks/use-cached-read";
import { readCacheKey } from "../lib/floor-read-cache";
import { formatBusinessDate } from "../../../lib/datetime";
import { formatQty } from "../../office/production/lib/format";
import "./floor-production.css";

type MyShift = components["schemas"]["MyShiftSummaryResponse"];

export function MyShiftScreen() {
  const { t, i18n } = useTranslation();
  const read = useCachedRead<MyShift>({
    queryKey: ["floor", "my-shift"],
    cacheKey: readCacheKey("my-shift", "self"),
    queryFn: async ({ signal }) =>
      unwrap(
        await client.GET("/v1/floor/me/shift-summary", { signal: floorReadSignal(signal) }),
        "my-shift",
      ),
  });

  const data = read.data;
  /** The module's own scale (3 dp), trailing zeros trimmed, separators in the
   * viewer's language — the same call the office S5 screen and the wall board
   * make on the identical wire value. */
  const qty = (value: string): string => formatQty(value, { locale: i18n.language, maxDp: 3 });

  return (
    <FloorScreen
      title={t("production.floor.myShift.title")}
      back={
        <Link to="/floor" className="floor-back">
          {t("production.floor.back")}
        </Link>
      }
    >
      {/* `revalidating` rides along like every other floor read (station-home,
          the count worksheet): a snapshot being re-checked says "checking for
          updates" rather than pretending it is current. This screen dropped
          the prop until the cached-with-age construction caught it. */}
      <SnapshotNote
        source={read.source}
        cachedAt={read.cachedAt}
        revalidating={read.revalidating}
      />
      {read.isLoading && data === undefined ? <FloorSkeleton rows={2} /> : null}

      {/* A read that did not come back is NOT a shift with no work in it.
          Offline-and-never-loaded and a failed request are different states and
          both say so — four zeros would read as "you have made nothing today". */}
      {data === undefined && !read.isLoading && read.source === "none" ? (
        read.online ? (
          <FloorError
            title={t("production.floor.myShift.errorTitle")}
            detail={t("production.floor.myShift.errorBody")}
            onRetry={read.refetch}
          />
        ) : (
          <FloorNeedsConnection what={t("production.floor.myShift.offlineWhat")} />
        )
      ) : null}

      {data !== undefined ? (
        <>
          <p
            className="floor-myshift-shift"
            data-testid="my-shift-window"
            data-shift-code={data.shift_definition_code ?? undefined}
          >
            {data.shift_definition_code === null
              ? t("production.floor.myShift.noShift")
              : t("production.floor.myShift.shiftLine", {
                  code: t(`production.shifts.shiftNames.${data.shift_definition_code}`, {
                    defaultValue: data.shift_definition_code,
                  }),
                  date: formatBusinessDate(data.shift_date, i18n.language),
                })}
          </p>

          <dl className="floor-myshift-grid" data-testid="my-shift-grid">
            <div className="floor-myshift-cell floor-myshift-primary">
              <dt>{t("production.floor.myShift.good")}</dt>
              <dd data-testid="my-shift-good">{qty(data.qty_good)}</dd>
            </div>
            <div className="floor-myshift-cell">
              <dt>{t("production.floor.myShift.scrap")}</dt>
              <dd data-testid="my-shift-scrap">{qty(data.qty_scrap)}</dd>
            </div>
            <div className="floor-myshift-cell">
              <dt>{t("production.floor.myShift.ops")}</dt>
              <dd data-testid="my-shift-ops">{data.ops_completed}</dd>
            </div>
            <div className="floor-myshift-cell">
              <dt>{t("production.floor.myShift.pieceQty")}</dt>
              <dd data-testid="my-shift-piece">{qty(data.piece_qty)}</dd>
            </div>
          </dl>
        </>
      ) : null}
    </FloorScreen>
  );
}
